import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { ClientBookingPaymentService } from 'service-api/src/services/client_booking_payment.service'
import { BookingReservationClientDto } from 'service-api/src/types/api'
import { useBookingContext } from '@providers/booking'
import { captureError } from '@shared/monitoring'
import { buildErrorMessage } from '@shared/error'
import { enqueueSnackbar } from 'notistack'

class PaymentInitializationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PaymentInitializationError'
  }
}

export const usePayment = (enabled = true, walletAmount?: number) => {
  const { accountName: locationName = '' } = useParams()
  const bookingCtx = useBookingContext()
  const [isLoading, setIsLoading] = useState(false)
  const hasInitializedForCurrentMount = useRef(false)

  useEffect(() => {
    if (!enabled) {
      // Reset guard so that the next time the form is mounted we re-initialize
      hasInitializedForCurrentMount.current = false
      return
    }

    const orderId = bookingCtx.state.data.reservation?.order?.id || ''
    if (!orderId) {
      return
    }

    // Ensure we initialize once per "mount" of the payment form
    if (hasInitializedForCurrentMount.current) {
      return
    }
    hasInitializedForCurrentMount.current = true

    setIsLoading(true)
    ClientBookingPaymentService.initializePayment(locationName, orderId, walletAmount)
      .then((data: BookingReservationClientDto) => {
        bookingCtx.dispatch({ type: 'SET_RESERVATION_DATA', payload: { value: data } })
        setIsLoading(false)
      })
      .catch((error: unknown) => {
        const errorMessage = buildErrorMessage(error)
        enqueueSnackbar(errorMessage, { variant: 'error' })
        captureError(
          new PaymentInitializationError(
            `Payment initialization failed for order ${orderId} with wallet amount ${walletAmount}: ${errorMessage}`
          )
        )
        setIsLoading(false)
      })
  }, [bookingCtx, bookingCtx.state.data.reservation?.order?.id, enabled, locationName, walletAmount])

  return {
    isLoading,
  }
}
